<script setup>
import axios from 'axios';
import { ref, onMounted, onUnmounted } from 'vue';
import DatePicker from '@parts/DatePicker.vue';
import { dateUtil } from '@functions/dateUtil.js';
import { modalUtil } from '@functions/modalUtil.js';
import { url } from '@/config/urls';
import { message } from '@functions/message.js';

// 外部定義メソッド
const { checkDateFormat, examValidDate } = dateUtil();
const { switchFromCurrentModal } = modalUtil();

const emit = defineEmits(['updateLoading', 'apiError', 'deliveryDateUpdated']);

const props = defineProps({
  goodsList: {
    type: Array,
    required: true
  },
  checkedList: {
    type: Array,
    required: true
  },
  allCheckedList: {
    type: Array,
    required: true
  },
  orderCartUpdateApiUrl: {
    type: String,
    default: url.api.orderCart.update
  }
});

// フォーム入力値
const formInputs = ref({
  deliverySpecifiedDate: ''
});

// エラー
const errors = ref({
  deliverySpecifiedDate: ''
});

// カート商品納品指定日変更
const onChangeDeliverySpecifiedDate = () => {
  // バリデーション
  deliverySpecifiedDateValidation();
}
// 納品指定日バリデーション処理
const deliverySpecifiedDateValidation = () => {
  errors.value.deliverySpecifiedDate = '';
  if (formInputs.value.deliverySpecifiedDate === '') {
    return;
  }
  // 日付フォーマットチェック
  if (!checkDateFormat(formInputs.value.deliverySpecifiedDate)) {
    errors.value.deliverySpecifiedDate = message.error.validation.date.format;
    return;
  }
  // 日付論理チェック
  if (!examValidDate(formInputs.value.deliverySpecifiedDate)) {
    errors.value.deliverySpecifiedDate = message.error.validation.date.validity;
    return;
  }
  const inputDate = new Date(formInputs.value.deliverySpecifiedDate);
  const today = new Date();

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  const oneMonthLater = new Date(today);
  oneMonthLater.setMonth(today.getMonth() + 1);
  oneMonthLater.setHours(23, 59, 59, 999);

  // 納品指定日範囲チェック１
  if (inputDate < tomorrow) {
    errors.value.deliverySpecifiedDate = message.error.validation.delivery_specified_date.min;
    return;
  }
  // 納品指定日範囲チェック２
  if (inputDate > oneMonthLater) {
    errors.value.deliverySpecifiedDate = message.error.validation.delivery_specified_date.max;
    return;
  }
}

// 納品指定日一括更新モーダル「変更する」押下時処理
const bulkUpdateCart = () => {
  emit('updateLoading', true);
  let deliverySpecifiedDate = formInputs.value.deliverySpecifiedDate;
  if (deliverySpecifiedDate) {
    deliverySpecifiedDate = deliverySpecifiedDate.replaceAll('/', '');
  }
  let goodsList = [];
  props.checkedList.forEach(janCode => {
    // 納品指定日指定可能な商品のみリストに追加
    if (props.goodsList.find(goods => goods.janCode === janCode).canSpecifyDate === true) {
      const goods ={
        janCode: janCode,
        quantity: null,
        deliverySpecifiedDate: deliverySpecifiedDate
      };
      goodsList.push(goods);
    }
  });
  const param = {
    goodsList: goodsList,
    isUpdateDeliverySpecifiedDate: true
  };
  axios.post(props.orderCartUpdateApiUrl, param)
    .then((response) => {
      emit('deliveryDateUpdated');
      switchFromCurrentModal('deliverySpecifiedDateUpdateCompleteModal');
      formInputs.value.deliverySpecifiedDate = '';
      errors.value.deliverySpecifiedDate = '';
    })
    .catch((error) => {
      emit('apiError', error);
    })
    .finally(() => {
      emit('updateLoading', false);
    });
}

// フォームを初期化する関数
const initializeForm = () => {
  formInputs.value.deliverySpecifiedDate = '';
  errors.value.deliverySpecifiedDate = '';
};

onMounted(() => {
  // 初回マウント時の初期化
  initializeForm();
  
  // モーダルが閉じたときのイベントリスナーを追加
  const modalElement = document.getElementById('deliverySpecifiedDateUpdateModal');
  if (modalElement) {
    modalElement.addEventListener('hidden.bs.modal', initializeForm);
  }
});

// コンポーネント破棄時にイベントリスナーを削除
onUnmounted(() => {
  const modalElement = document.getElementById('deliverySpecifiedDateUpdateModal');
  if (modalElement) {
    modalElement.removeEventListener('hidden.bs.modal', initializeForm);
  }
});
</script>

<template>
  <div class="modal com_modal2 fade" id="deliverySpecifiedDateUpdateModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <img src="/front/assets/common/img/icon_c_check1.png" alt="" class="icon_c_check1">
          <h3 class="com_fs18 com_strong1 com_center1 com_mgb0">納品指定日を変更します</h3>
          <div class="com_mgt10">
            <div class="datepicker-group1">
              <label for="js-datepicker1">納品指定日</label>
              <DatePicker
                  v-model="formInputs.deliverySpecifiedDate"
                  @update:modelValue="onChangeDeliverySpecifiedDate()"
                  :class="{ error: errors.deliverySpecifiedDate !== '' }"
                  identifier="js-deliverySpecifiedDate-datepicker"
              ></DatePicker>
            </div>
            <div v-if="errors.deliverySpecifiedDate !== ''" class="simple_card_error2">
              <span class="com_fs12 com_strong1 com_color4">
                {{ errors.deliverySpecifiedDate }}
              </span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn com_btn1" @click="bulkUpdateCart" :disabled="errors.deliverySpecifiedDate !== ''">変更する</button>
          <button type="button" class="btn com_btn2" data-bs-dismiss="modal">いいえ</button>
        </div>
      </div>
    </div>
  </div>
</template>